import { useRef, useState } from 'react';
import { Sun, Zap, Leaf, Layers } from 'lucide-react';
import Map, { NavigationControl, Source, Layer } from 'react-map-gl/maplibre';
import type { MapRef } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import '@/styles/map.css';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useTerritoryStore } from '@/stores/territoryStore';
import { useSolar } from '@/hooks/useSolar';
import { useMapRecenter } from '@/hooks/useMapRecenter';
import { IndicatorCard } from '@/components/dashboard/IndicatorCard';
import { DataAttribution } from '@/components/map/DataAttribution';
import { authenticatedMapRequest, INITIAL_VIEW, MAP_STYLE, territoryTileUrl } from '@/config';
import { ProductionDataUnavailable } from '@/components/common/ProductionDataUnavailable';
import { isDataUnavailable } from '@/services/dataAvailability';

// ── Design tokens (inlined — no theme.ts import) ─────────────────────────────

const NAVY = '#1b2559';
const MARIANNE = '#e1000f';
const SUCCESS = '#1f8a5b';
const DANGER_BG = '#fdecec';

/** Solar ramp — §5 Cadastre solaire: #fff3c4→#ffd24a→#ff8c1a→#e1000f */
const SOLAR_RAMP = {
  low:     '#fff3c4',
  midLow:  '#ffd24a',
  midHigh: '#ff8c1a',
  high:    '#e1000f',
};

/** Éligibilité donut colours: Favorable / Non favorable. */
const ELIGIB_COLORS = ['#ffd24a', '#8b93a9'];

/** Month labels (Jan → Déc). */
const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

// ── CadastreSolaire ───────────────────────────────────────────────────────────

/**
 * Cadastre solaire — 5e écran Terravision.
 * Aide à la décision : gisement PV par toiture, profil mensuel, éligibilité.
 *
 * Sources : BDTOPO (bâtiments), API PVGIS (Commission Européenne),
 * mix électrique FR (0,0556 kg CO₂/kWh — ADEME 2024).
 * Gisement par toiture (solar_potential_kwh) et KPIs réels (API /solar) ;
 * potentiel estimé sans LiDAR fin. Éligibilité ABF et top toitures : DEMO.
 */
export function CadastreSolaire() {
  const { activeTerritory } = useTerritoryStore();
  const insee = activeTerritory?.code_insee ?? null;
  const { data, isLoading, error, refetch } = useSolar(insee);

  const mapRef = useRef<MapRef>(null);
  const [mapReady, setMapReady] = useState(false);
  useMapRecenter(mapRef, mapReady);

  // ── No territory selected ────────────────────────────────────────────────
  if (!activeTerritory) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <p className="font-sans text-sm text-muted">
          Sélectionnez un territoire pour accéder au Cadastre solaire.
        </p>
      </div>
    );
  }


  if (isDataUnavailable(error)) {
    return (
      <ProductionDataUnavailable
        feature="Cadastre solaire"
        requiredSources="PVGIS, BD TOPO et LiDAR HD avec manifeste de provenance"
      />
    );
  }
  // ── Error ─────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div
          className="rounded-panel flex flex-col gap-2 px-5 py-4 text-sm font-medium"
          style={{ background: DANGER_BG, color: MARIANNE }}
          role="alert"
        >
          <span>Erreur lors du chargement des données solaires.</span>
          <button
            type="button"
            className="self-start font-semibold underline"
            onClick={() => refetch()}
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (isLoading || !data) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <p className="font-sans text-sm text-muted animate-pulse">
          Chargement des données solaires…
        </p>
      </div>
    );
  }

  // ── Derived values ─────────────────────────────────────────────────────────

  // Éligibilité = part de toitures à orientation favorable (S/SE/SW), issue du
  // backend (data.favorable_pct). Le reste = orientations non favorables. Aucune
  // donnée fabriquée (l'ancien découpage « Contraint ABF » n'avait pas de source).
  const favorablePct = Math.round(data.favorable_pct);
  const eligibiliteData = [
    { name: 'Orientation favorable',     value: favorablePct },
    { name: 'Orientation non favorable', value: Math.max(0, 100 - favorablePct) },
  ];

  const monthlyData = MONTHS.map((month, i) => ({
    month,
    gwh: data.monthly_gwh[i] ?? 0,
  }));

  // Peak monthly production (real — derived from the /solar monthly profile).
  const peakMonthlyGwh = data.monthly_gwh.length
    ? Math.max(...data.monthly_gwh)
    : 0;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-4 p-5">

      {/* ── Bandeau solaire ─────────────────────────────────────────────────── */}
      <div
        className="flex items-center gap-4 rounded-panel px-5 py-3.5 flex-wrap"
        style={{ background: 'linear-gradient(100deg,#92400e,#b45309,#d97706)' }}
        role="region"
        aria-label="Cadastre solaire — gisement PV"
      >
        <Sun size={22} aria-hidden="true" className="flex-shrink-0 text-white/80" />
        <span className="font-display text-[15px] font-extrabold text-white">
          Cadastre solaire — Gisement photovoltaïque
        </span>
        <span className="text-[13px] text-white/80">
          Estimation du potentiel PV par toiture · BDTOPO × PVGIS
        </span>
      </div>

      {/* ── Rangée KPI (4 cards) ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-4 gap-3">
        <IndicatorCard
          label="Potentiel solaire"
          value={data.potential_gwh}
          unit="GWh/an"
          icon={<Zap size={15} />}
          color="#d97706"
        />
        <IndicatorCard
          label="Toitures favorables"
          value={data.favorable_pct}
          unit="%"
          icon={<Sun size={15} />}
          color={NAVY}
        />
        <IndicatorCard
          label="CO₂ évité"
          value={data.co2_avoided_t}
          unit="t CO₂/an"
          icon={<Leaf size={15} />}
          color={SUCCESS}
        />
        <IndicatorCard
          label="Pic de production"
          value={Number(peakMonthlyGwh.toFixed(2))}
          unit="GWh (mois)"
          icon={<Layers size={15} />}
          color="#ef7d18"
        />
      </div>

      {/* ── Grille 1.55fr / 1fr ──────────────────────────────────────────────── */}
      <div className="grid gap-4" style={{ gridTemplateColumns: '1.55fr 1fr', minHeight: '480px' }}>

        {/* ── LEFT : Carte de gisement solaire ─────────────────────────────── */}
        <div className="rounded-panel border border-line bg-white shadow-card overflow-hidden flex flex-col">
          <div className="border-b border-line px-4 py-3 flex items-center justify-between">
            <div>
              <h3 className="font-display text-[14px] font-bold text-ink">
                Carte de gisement solaire
              </h3>
              <p className="text-[11.5px] text-faint">
                Potentiel PV par toiture — couche bâtiments colorée par solar_potential_kwh
              </p>
            </div>
          </div>

          {/* Map — buildings colored by solar_potential_kwh via solar ramp */}
          <div style={{ flex: 1, minHeight: 280, position: 'relative' }}>
            <Map
              ref={mapRef}
              initialViewState={INITIAL_VIEW}
              style={{ width: '100%', height: '100%' }}
              mapStyle={MAP_STYLE}
              transformRequest={authenticatedMapRequest}
              onLoad={() => setMapReady(true)}
            >
              <NavigationControl position="top-right" />
              <DataAttribution />

              {/*
               * Source : Martin vector tiles — buildings layer.
               * Solar ramp applied on solar_potential_kwh (kWh/year), a real
               * per-building field computed by the backend solar cadastre.
               * If the field is null (not yet computed for that building),
               * coalesce defaults to 0 → low-end ramp colour (#fff3c4).
               * REQUIS : la propriété `solar_potential_kwh` doit être exposée
               * dans la source `buildings` de martin/config.yaml.
               */}
              <Source
                id="solar-buildings"
                type="vector"
                tiles={[territoryTileUrl(activeTerritory.code_insee, 'buildings')]}
                minzoom={12}
                maxzoom={18}
              >
                <Layer
                  id="solar-buildings-fill"
                  type="fill-extrusion"
                  source-layer="buildings"
                  paint={{
                    'fill-extrusion-color': [
                      'interpolate',
                      ['linear'],
                      ['coalesce', ['to-number', ['get', 'solar_potential_kwh']], 0],
                      // Realistic per-roof annual PV potential (kWh/an) — the bulk of
                      // buildings sit in ~9 000–110 000 kWh, so the ramp spreads the
                      // real distribution across the full colour range.
                      5000,   SOLAR_RAMP.low,
                      25000,  SOLAR_RAMP.midLow,
                      50000,  SOLAR_RAMP.midHigh,
                      90000,  SOLAR_RAMP.high,
                    ],
                    'fill-extrusion-height': ['coalesce', ['to-number', ['get', 'height']], 4],
                    'fill-extrusion-opacity': 0.85,
                  }}
                />
              </Source>
            </Map>

            {/* Solar ramp legend */}
            <div
              style={{
                position: 'absolute',
                bottom: 12,
                left: 12,
                background: 'rgba(255,255,255,0.92)',
                borderRadius: 10,
                border: '1px solid #e4e8f0',
                padding: '8px 12px',
                boxShadow: '0 8px 24px -16px rgba(17,25,58,.18)',
                backdropFilter: 'blur(4px)',
              }}
            >
              <p style={{ fontSize: 10.5, fontWeight: 600, color: '#11193a', marginBottom: 6 }}>
                Potentiel PV
              </p>
              <div
                style={{
                  height: 10,
                  width: 96,
                  borderRadius: 9999,
                  background: `linear-gradient(to right, ${SOLAR_RAMP.low}, ${SOLAR_RAMP.midLow}, ${SOLAR_RAMP.midHigh}, ${SOLAR_RAMP.high})`,
                }}
                aria-hidden="true"
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                <span style={{ fontSize: 10, color: '#8b93a9' }}>Faible</span>
                <span style={{ fontSize: 10, color: '#8b93a9' }}>Élevé</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT : Donut + profil mensuel + top toitures ─────────────────── */}
        <div className="flex flex-col gap-3">

          {/* Éligibilité PV — donut */}
          <div className="rounded-panel border border-line bg-white shadow-card p-4">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-display text-[13px] font-bold text-ink">
                Orientation des toitures
              </h4>
            </div>
            <div className="flex items-center gap-3" style={{ height: 120 }}>
              <ResponsiveContainer width="55%" height="100%">
                <PieChart>
                  <Pie
                    data={eligibiliteData}
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={50}
                    dataKey="value"
                    startAngle={90}
                    endAngle={-270}
                    paddingAngle={1}
                    stroke="none"
                  >
                    {eligibiliteData.map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={ELIGIB_COLORS[index] ?? '#ccc'} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      fontSize: 11,
                      borderRadius: 8,
                      border: '1px solid #e4e8f0',
                      boxShadow: '0 4px 12px rgba(17,25,58,.1)',
                    }}
                    formatter={(value) => {
                      if (typeof value === 'number') return `${value}%`;
                      return String(value);
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>

              {/* Donut legend */}
              <div className="flex flex-col gap-1.5 flex-1">
                {eligibiliteData.map((entry, i) => (
                  <div key={entry.name} className="flex items-center gap-1.5">
                    <span
                      className="h-2.5 w-2.5 flex-shrink-0 rounded-[2px]"
                      style={{ background: ELIGIB_COLORS[i] ?? '#ccc' }}
                      aria-hidden="true"
                    />
                    <span className="flex-1 text-[11px] text-muted">{entry.name}</span>
                    <span className="font-mono text-[11px] font-semibold text-ink">
                      {entry.value}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Profil mensuel de production — area chart */}
          <div className="rounded-panel border border-line bg-white shadow-card p-4">
            <h4 className="font-display text-[13px] font-bold text-ink mb-2">
              Profil mensuel (GWh)
            </h4>
            <div style={{ height: 120 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={monthlyData}
                  margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
                >
                  <defs>
                    <linearGradient id="solarAreaGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={SOLAR_RAMP.midLow} stopOpacity={0.8} />
                      <stop offset="95%" stopColor={SOLAR_RAMP.midLow} stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e8f0" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 9.5, fill: '#8b93a9' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 9.5, fill: '#8b93a9' }}
                    tickLine={false}
                    axisLine={false}
                    width={42}
                    tickFormatter={(v: number) => v.toFixed(3)}
                  />
                  <Tooltip
                    contentStyle={{
                      fontSize: 11,
                      borderRadius: 8,
                      border: '1px solid #e4e8f0',
                      boxShadow: '0 4px 12px rgba(17,25,58,.1)',
                    }}
                    formatter={(value) => {
                      if (typeof value === 'number') return `${value.toFixed(4)} GWh`;
                      return String(value);
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="gwh"
                    name="Production"
                    stroke={SOLAR_RAMP.midHigh}
                    strokeWidth={2}
                    fill="url(#solarAreaGrad)"
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

        </div>
      </div>

      {/* ── Sources de données ──────────────────────────────────────────────── */}
      <p className="pb-1 text-[10.5px] text-faint">
        Sources : BDTOPO (bâtiments), API PVGIS (irradiation, Commission Européenne).
        Gisement par toiture et KPIs issus du cadastre solaire backend ; potentiel
        estimé sans LiDAR fin.
        CO₂ : mix électrique FR moyen (0,0556 kg CO₂/kWh, ADEME 2024).
        Orientation des toitures issue de l&apos;axe principal du bâti (BDTOPO).
      </p>
    </div>
  );
}
